import { Button, ButtonContainer, Card, FormHeading, Layout } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationSummary } from '../../../components/ApplicationSummary/ApplicationSummary';
import { SummaryConsentForm } from '../../../components/SummaryConsentForm/SummaryConsentForm';
import { FlowTypeCheckerFn, useFlowTypeChecker } from '../../../services/common/miscUtils';
import { journeyDataSelectors } from '../../../services/redux/features/journeyData.slice';
import { uiStateActions, uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { useNavigate } from '../../../services/routing';
import { FlowTypeEnum } from '../../../types/forms';
import { newQuotationSliceActions, WizardPagesEnum } from '../newQuotation.slice';
import { CustomerData_FP_LtdPlcUnlimitedOther } from './CustomerDataFinancialProduct/business/ltdPlcUnlimitedOther';
import { CustomerData_FP_Partnership } from './CustomerDataFinancialProduct/business/partnership';
import { CustomerData_FP_SoleTrader } from './CustomerDataFinancialProduct/business/soleTrader';
import { CustomerDataOperatingLeaseOtherBusiness } from './CustomerDataOperatingLease/business/otherBusiness';
import { CustomerDataOperatingLeaseSoleTrader } from './CustomerDataOperatingLease/business/soleTrader';
import { BusinessCustomerTypesEnum } from './customerDataPage.model';
import { CustomerDataPrivate } from './CustomerDataPrivate/CustomerDataPrivate';
import { CustomerDataPrivateLease } from './CustomerDataPrivateLease/CustomerDataPrivateLease';
import { CustomerDataServicePlan } from './CustomerDataServicePlan/CustomerDataServicePlan';
import { CustomerDataServicePlanBusinessLtdPlc } from './CustomerDataServicePlanBusinessLtdPlc/CustomerDataServicePlanBusinessLtdPlc';
import { CustomerDataServicePlanBusinessOther } from './CustomerDataServicePlanBusinessOther/CustomerDataServicePlanBusinessOther';
import { CustomerDataServicePlanBusinessPartnership } from './CustomerDataServicePlanBusinessPartnership/CustomerDataServicePlanBusinessPartnership';
import { CustomerDataServicePlanBusinessSoleTrader } from './CustomerDataServicePlanBusinessSoleTrader/CustomerDataServicePlanBusinessSoleTrader';

const renderBusinessCustomerForms = (flowTypeIs: FlowTypeCheckerFn) => {
  if (flowTypeIs('financialProduct')) {
    return (
      <>
        {flowTypeIs('soleTrader') && <CustomerData_FP_SoleTrader />}
        {flowTypeIs('partnership') && <CustomerData_FP_Partnership />}
        {flowTypeIs('other', 'ltd', 'plc', 'unlimited') && <CustomerData_FP_LtdPlcUnlimitedOther />}
      </>
    );
  }

  if (flowTypeIs('servicePlan')) {
    return (
      <>
        {flowTypeIs('soleTrader') && <CustomerDataServicePlanBusinessSoleTrader />}
        {flowTypeIs('partnership') && <CustomerDataServicePlanBusinessPartnership />}
        {flowTypeIs('other') && <CustomerDataServicePlanBusinessOther />}
        {flowTypeIs('ltd', 'plc', 'unlimited') && <CustomerDataServicePlanBusinessLtdPlc />}
      </>
    );
  }

  if (flowTypeIs('operatingLease')) {
    return (
      <>
        {flowTypeIs('soleTrader') && <CustomerDataOperatingLeaseSoleTrader />}
        {!flowTypeIs('soleTrader') && <CustomerDataOperatingLeaseOtherBusiness />}
      </>
    );
  }

  return null;
};

const renderPrivateCustomerForms = (flowTypeIs: FlowTypeCheckerFn) => {
  if (flowTypeIs('financialProduct')) {
    return <CustomerDataPrivate />;
  }

  if (flowTypeIs('servicePlan')) {
    return <CustomerDataServicePlan />;
  }

  if (flowTypeIs('privateLease')) {
    return <CustomerDataPrivateLease />;
  }

  return null;
};

const getFormHeading = (flowTypeIs: FlowTypeCheckerFn, businessCustomerType?: BusinessCustomerTypesEnum) => {
  if (flowTypeIs('consumer')) {
    return t('customerDataPage:customer1Header:private1');
  }
  if (flowTypeIs('servicePlan')) {
    return t(`customerDataPage:customer1Header.${FlowTypeEnum.servicePlan}`);
  }

  return t(`customerDataPage:customer1Header.${businessCustomerType}`);
};

export const CustomerDataPage: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const businessCustomerType = useSelector(journeyDataSelectors.businessCustomerType);

  const flowTypeIs = useFlowTypeChecker();

  // TODO: restore in-data-storage persistance at later point in time
  // const { handleSaveSession } = useSaveSession();

  // TODO: reintroduce together with reworked application prefill solution
  // const snCClosed = useSelector(uiStateSelectors.snCClosed);

  const hideFormHeading = flowTypeIs.every('consumer', 'servicePlan');

  const isSummaryMode = useSelector(uiStateSelectors.isSummaryMode);
  const allFormsSubmitted = useSelector(uiStateSelectors.allFormsSubmitted);
  const isAnySectionInEditMode = useSelector(uiStateSelectors.isAnySectionInEditMode);

  const onContinue = () => {
    if (isSummaryMode) {
      if (flowTypeIs('servicePlan')) {
        navigate.dealerSummaryPage();
        return;
      }

      dispatch(newQuotationSliceActions.wizardNavigateToPage(WizardPagesEnum.affordabilityDocuments));
    } else {
      dispatch(uiStateActions.setSummaryMode(true));
    }
  };

  // TODO: remove in PC-3684
  // eslint-disable-next-line no-console
  console.log('isSummaryMode: ', isSummaryMode);

  return (
    <>
      <Card title="" className="u-mb">
        <ApplicationSummary isDefaultOpen={false} />
      </Card>

      {/* TODO: reintroduce together with reworked application prefill solution */}
      {/* {!snCClosed && (
        <Card title="" id="snc-init-lead-form" className="u-mb">
          <SnCInitialization />
        </Card>
      )} */}

      <Card title="">
        <Layout>
          {!hideFormHeading && (
            <Layout.Item default="1/2">
              <FormHeading className="u-mb-none">{getFormHeading(flowTypeIs, businessCustomerType)}</FormHeading>
            </Layout.Item>
          )}
        </Layout>

        {flowTypeIs('consumer') && renderPrivateCustomerForms(flowTypeIs)}

        {flowTypeIs('business') && renderBusinessCustomerForms(flowTypeIs)}
        {isSummaryMode && (
          <Layout className="u-mb u-mt-large">
            <Layout.Item>
              <SummaryConsentForm />
            </Layout.Item>
          </Layout>
        )}
      </Card>

      <Layout center className="u-mt-xsmall">
        <Layout.Item default={isAnySectionInEditMode ? '1/1' : '1/2'}>
          <ButtonContainer center>
            <Button
              testId="button_goBack"
              secondary
              onClick={() => {
                dispatch(newQuotationSliceActions.wizardNavigateToPage(WizardPagesEnum.prospect));
              }}
            >
              {t('customerDataPage:navigation.back')}
            </Button>

            {!isAnySectionInEditMode && (
              <Button testId="button_continue" onClick={onContinue} disabled={!allFormsSubmitted}>
                {isSummaryMode
                  ? t('customerDataPage:navigation.submitForActivation')
                  : t('customerDataPage:navigation.review')}
              </Button>
            )}
          </ButtonContainer>
        </Layout.Item>
      </Layout>
    </>
  );
};
